import { Inject, Injectable, InjectionToken } from '@angular/core';
import { PaginatedState, PaginatedStateLoadingMode, PaginatedStateModel } from '@shared/ui';
import { LabelLanguages } from '@telespot/domain';
import { Observable } from 'rxjs';

export interface LabelData {
  /*
   * Required by PaginatedStore (same as uuid)
   */
  id: string;

  /*
   * Using uuid for label identification
   */
  uuid: string;

  /*
   * Localized values keyed by language code. Mirrors `SaveLabelInput.values`
   * (English is required, other languages optional) so the UI never has to
   * adapt between an array shape and a dict shape.
   */
  languages: LabelLanguages;

  /*
   * True for labels that exist only client-side and have not yet been persisted.
   */
  pending?: boolean;
}

/*
 * Local edit applied to a label that DOES exist in the database. We carry both
 * halves so the listing flow can hide the stale DB row and place the edited
 * version at its new sorted position, and so the total count remains exact
 * even when the edit moves the label in/out of the active search match.
 */
export interface LabelOverride {
  original: LabelData;
  override: LabelData;
}

export interface LabelStateModel extends PaginatedStateModel<LabelData> {
  /*
   * Store-specific state
   */
  saving: boolean;

  /*
   * Labels created in the UI but not yet persisted. Source of truth — the items
   * adapter is just a per-page snapshot that may include some of these.
   */
  pendingLabels: LabelData[];

  /*
   * Local edits to persisted labels, keyed by uuid. Until persisted, the listing
   * flow shows the override in place of the DB row sharing this uuid.
   */
  labelOverrides: Record<string, LabelOverride>;

  /*
   * Exceptions from the last save pass, keyed by the in-memory uuid of the row
   * that failed (placeholder uuid for pending labels, `original.uuid` for
   * overrides). A cascade batch can fail several rows at once, so this is a map
   * rather than a single slot. Stored as the raw `Error`, not a message, so a
   * consumer can dispatch on the exception subclass and map it to copy of its
   * own.
   */
  saveExceptions: Record<string, Error>;
}

/**
 * A failed save row, normalized: whose label failed, and why.
 *
 * The shape every state that records label-related save failures exposes, so a
 * consumer never has to know how the owning state keys its exceptions internally.
 *
 * `rowId` is a per-row identity minted by the state that owns the failure. It
 * exists so two failures of the SAME label (on different method types) stay
 * distinguishable, which a bare `labelUuid` could not do. Consumers compare it
 * and nothing else — only the owning state knows what it is made of.
 */
export interface LabelSaveFailure {
  rowId: string;
  labelUuid: string;
  error: Error;
}

export const LABEL_PAGINATION_MODE = new InjectionToken<PaginatedStateLoadingMode>('LABEL_PAGINATION_MODE');

@Injectable()
export class LabelState extends PaginatedState<LabelData, LabelStateModel> {
  constructor(@Inject(LABEL_PAGINATION_MODE) paginationMode: PaginatedStateLoadingMode) {
    // Consumers pick the mode: the pipeline editor's autocomplete dropdowns want
    // CONTINUOUS (pages accumulate as the user scrolls the suggestion list), while
    // the labels management table wants PAGINATED (each page replaces the last).
    super(
      { loadingMode: paginationMode },
      { saving: false, pendingLabels: [], labelOverrides: {}, saveExceptions: {} }
    );
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly saving$ = this.select((state) => state.saving);
  public readonly pendingLabels$ = this.select((state) => state.pendingLabels);
  public readonly labelOverrides$ = this.select((state) => state.labelOverrides);
  public readonly saveExceptions$ = this.select((state) => state.saveExceptions);

  /**
   * Failures in the normalized shape consumers read. Rows here are keyed by the
   * label uuid itself, so the row identity IS the uuid — but a consumer does not
   * need to know that.
   */
  public readonly saveFailures$: Observable<LabelSaveFailure[]> = this.select(
    this.saveExceptions$,
    (saveExceptions): LabelSaveFailure[] =>
      Object.entries(saveExceptions).map(([uuid, error]) => ({ rowId: uuid, labelUuid: uuid, error }))
  );

  // Expose selectors with original names for backwards compatibility
  public readonly labels$ = this.items$;
  public readonly nextLabelToken$ = this.nextToken$;
  public readonly previousLabelTokens$ = this.previousTokens$;
  public readonly labelsLoaded$ = this.itemsLoaded$;
  public readonly noLabels$ = this.noItems$;

  /*
   * uuid → languages lookup across all three label slots, so a consumer holding
   * only a uuid (e.g. a `saveExceptions` key) can resolve a display name.
   * Overrides are indexed under `original.uuid` with the ORIGINAL languages —
   * the uuid the save cascade emits on a failing override row.
   */
  public readonly labelLanguagesByUuid$ = this.select(
    this.labels$,
    this.pendingLabels$,
    this.labelOverrides$,
    (labels, pendingLabels, labelOverrides): ReadonlyMap<string, LabelLanguages> => {
      const byUuid = new Map<string, LabelLanguages>();
      for (const label of labels) byUuid.set(label.uuid, label.languages);
      for (const pending of pendingLabels) byUuid.set(pending.uuid, pending.languages);
      for (const { original } of Object.values(labelOverrides)) byUuid.set(original.uuid, original.languages);
      return byUuid;
    }
  );

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));

  public readonly updateLabelInList = this.updater((state, updatedLabel: LabelData) => ({
    ...state,
    items: this.itemsAdapter.upsertOne(updatedLabel, state.items),
    saving: false,
  }));

  public readonly addPendingLabel = this.updater((state, label: LabelData) => {
    const normalized: LabelData = { ...label, id: label.uuid, pending: true };
    const without = state.pendingLabels.filter((p) => p.uuid !== normalized.uuid);
    return { ...state, pendingLabels: [...without, normalized] };
  });

  public readonly removePendingLabel = this.updater((state, uuid: string) => ({
    ...state,
    pendingLabels: state.pendingLabels.filter((p) => p.uuid !== uuid),
  }));

  public readonly clearPendingLabels = this.updater((state) => ({ ...state, pendingLabels: [] }));

  public readonly setLabelOverride = this.updater((state, entry: LabelOverride) => {
    const uuid = entry.original.uuid;
    const normalizedOverride: LabelData = { ...entry.override, id: uuid, uuid, pending: undefined };
    return {
      ...state,
      labelOverrides: { ...state.labelOverrides, [uuid]: { original: entry.original, override: normalizedOverride } },
    };
  });

  public readonly removeLabelOverride = this.updater((state, uuid: string) => {
    if (!(uuid in state.labelOverrides)) return state;
    const labelOverrides = { ...state.labelOverrides };
    delete labelOverrides[uuid];
    return { ...state, labelOverrides };
  });

  public readonly clearLabelOverrides = this.updater((state) => ({ ...state, labelOverrides: {} }));

  // Override setError to also clear saving state
  public override readonly setError = this.updater((state, error: string | undefined) => ({
    ...state,
    error,
    saving: false,
    loading: false,
  }));

  // Record one row's save failure keyed by its uuid. Does NOT touch `saving` —
  // bulk callers (the cascade) flip `saving` once after the whole batch settles;
  // the standalone `saveLabel` path clears it explicitly.
  public readonly recordSaveException = this.updater((state, entry: { uuid: string; error: Error }) => ({
    ...state,
    saveExceptions: { ...state.saveExceptions, [entry.uuid]: entry.error },
  }));

  // Clear all recorded save failures. No-op when already empty so a fresh `{}`
  // reference can't fire a spurious selector emit (which under fakeAsync can
  // leave a pending timer in the queue).
  public readonly clearSaveExceptions = this.updater((state) =>
    Object.keys(state.saveExceptions).length === 0 ? state : { ...state, saveExceptions: {} }
  );
}
